import { cloneDeep, isEqual } from 'lodash';
import { newNames } from '@/utils/resultsHelper';
import uuidGenerator from '@/utils/uuidGenerator';
import { saveStates } from '@/constants/lifecycle';
import { pruneEntityErrors, pruneFieldError } from '@/utils/errors';
import queryVisualisation from '../../visualisations/state/state';
import {
  mergeClauses,
  newCohortClause,
  removeUUIDsFromCohortClauses,
  addUUIDsToCohortClause,
} from '../../clauses/state/state';
import cohortProfile from '../../profile/state/state';
import { uniqueDictionariesInClauses } from '../../state/shared';

const newCohortQuery = (dataset, dictionaries, name, { visualisations }) => {
  const names = newNames('Clause', [], dictionaries.length);
  const copy = cloneDeep(dataset);
  delete copy.settings;

  return ({
    name,
    description: '',
    uuid: uuidGenerator.generate(),
    activeDictionaryCode: dictionaries[0].code,
    clauses: dictionaries.map((d, index) => newCohortClause(d, names[index])),
    visualisations: visualisations.map(queryVisualisation),
    profiles: dictionaries.map((d) => cohortProfile(d.code)),
    dataset: copy,
  });
};

export const activeQuery = () => ({
  open: false,
  code: '',
  category: '',
  uuid: '',
  name: '',
  description: '',
  ...saveStates,
});

export const pruneCohortQueryErrors = (errors, { queryUUID }, field = null) => (
  pruneEntityErrors(errors, ['query'], (e) => e.context.queryUUID !== queryUUID
        || (e.context.queryUUID === queryUUID && pruneFieldError(e, field)))
);

export const addUUIDsToCohortQuery = (
  query,
  addPlaceholder = false,
  visualisations = [],
  removeId = false,
) => {
  const copy = cloneDeep(query);
  const codes = uniqueDictionariesInClauses(copy).map((dict) => dict.code);
  const dictionaries = query.dataset.dictionaries.filter((r) => (codes.includes(r.code)));
  const clauses = copy.clauses.map((clause) => addUUIDsToCohortClause(clause, addPlaceholder, removeId));

  if (addPlaceholder) {
    const names = newNames('Clause', copy.clauses.filter((c) => !c.isNew)
      .map((c) => c.name), dictionaries.length);
    const placeholders = dictionaries.map((d, index) => newCohortClause(d, names[index]));
    placeholders.forEach((placeholder) => clauses.push(placeholder));
  }

  if (removeId) {
    delete copy.id;
  }

  copy.uuid = uuidGenerator.generate();
  copy.activeDictionaryCode = dictionaries[0].code;
  copy.clauses = clauses;
  copy.visualisations = visualisations.map(queryVisualisation);
  copy.profiles = dictionaries.map((d) => cohortProfile(d.code));

  return copy;
};

const removeUUIDsFromCohortQuery = (query, removeId = false) => {
  const copy = {
    name: query.name,
    description: query.description,
  };

  if (!removeId && query.id) {
    copy.id = query.id;
  }

  if (query.clauses) {
    copy.clauses = removeUUIDsFromCohortClauses(query.clauses, removeId);
  }

  if (query.toBeDeleted) {
    copy.toBeDeleted = true;
  }

  Object.keys(copy).forEach((k) => copy[k] == null && delete copy[k]);

  return copy;
};

export const removeUUIDsFromQueries = ({ queries }, removeId = false) => (
  queries.filter((q) => !q.isNew).map((q) => removeUUIDsFromCohortQuery(q, removeId))
);

export const addUUIDsToQueries = ({ queries }, { visualisations }) => {
  if (!queries) {
    return [];
  }
  return queries.map((q) => addUUIDsToCohortQuery(q, true, visualisations));
};

const mergeQuery = (target, index, query, sourceQuery, targetQueries) => {
  const {
    visualisations, profiles, clauses, status,
  } = cloneDeep(target.queries[index]);

  return {
    ...query,
    clauses: mergeClauses(sourceQuery.clauses, clauses, targetQueries[index].clauses, query),
    visualisations,
    profiles,
    status,
  };
};

export const mergeQueries = (source, target, queries) => {
  const sourceQueries = removeUUIDsFromQueries(source, true);
  const targetQueries = removeUUIDsFromQueries(target, true);

  return queries.map((query, index) => {
    const sourceQuery = sourceQueries[index];
    const match = targetQueries.findIndex((y) => isEqual(y, sourceQuery));
    return (match >= 0) ? mergeQuery(target, match, query, sourceQuery, targetQueries) : query;
  });
};

export default newCohortQuery;
